import { CSSProperties } from 'react';
// configs
import { WPP_LINK_PLG, WPP_LINK_TRADICIONAL } from 'config/flags';
// components
import { MessageWrapper } from '@commons/components/MessageWrapper';
import { TimerWithProgress } from '@commons/components/TimerWithProgress';
// utisl && hooks
import { useUser } from 'utils/hooks';
// styles
import styles from './TrialTimerMessage.module.css';
import { t } from 'i18next';

const TrialTimerMessage = ({ onClose, daysLeft, showClose }) => {
    const { user } = useUser({});

    const getStyles = (): CSSProperties => {
        if (daysLeft > 7) {
            return {
                borderBottom: `1px solid var(--Semantic-Success-100, #ddf6e2)`,
                background: `linear-gradient(90deg, #ebf9f3 0%, #fff 100%)`,
                boxShadow: `0px 2px 7.5px -3px rgba(7, 173, 75, 0.2), 0px 0px 6px -2px rgba(7, 173, 75, 0.2)`,
            };
        } else if (daysLeft > 4) {
            return {
                borderBottom: `1px solid var(--Semantic-Warning-100, #FFEA9F)`,
                background: `linear-gradient(90deg, #FFF9E6 0%, #FFF 100%)`,
                boxShadow: `0px 2px 7.5px -3px rgba(255, 199, 0, 0.20), 0px 0px 6px -2px rgba(255, 199, 0, 0.20)`,
            };
        } else if (daysLeft === 0) {
            return {
                borderBottom: `1px solid var(--Discount-300, #FFCDAD)`,
                background: '#FFECE0',
                boxShadow: `0px 0px 15px -3px rgba(255, 180, 133, 0.20), 0px 0px 6px -2px rgba(255, 180, 133, 0.20)`,
            };
        } else {
            return {
                borderBottom: `1px solid var(--Discount-300, #FFCDAD)`,
                background: `linear-gradient(90deg, #FFECE0 0%, #FFF 100%)`,
                boxShadow: `0px 0px 15px -3px rgba(255, 180, 133, 0.20), 0px 0px 6px -2px rgba(255, 180, 133, 0.20)`,
            };
        }
    };

    return (
        <MessageWrapper customStyles={getStyles()}>
            <div className={styles['wrapper-principal-content']}>
                <TimerWithProgress
                    maxDays={30}
                    daysLeft={daysLeft}
                    width={40}
                    height={40}
                    withTextDay={true}
                    withPopover={false}
                    withStrongColor={false}
                    backgroundColor={'#fff'}
                />

                <div className={styles['messageContent']}>
                    <p className={styles['title']}>
                        {daysLeft > 7
                            ? t('home.timerTrial.alert.title.init')
                            : daysLeft > 4
                            ? t('home.timerTrial.alert.title.middle', { value: daysLeft })
                            : daysLeft === 0
                            ? t('home.timerTrial.alert.title.finish')
                            : daysLeft === 1
                            ? t('home.timerTrial.alert.title.lastOneDay')
                            : t('home.timerTrial.alert.title.lastDay', { value: daysLeft })}
                    </p>
                    <p className={styles['subtitle']}>
                        {daysLeft > 7
                            ? t('home.timerTrial.alert.subtitle.init', { value: daysLeft })
                            : daysLeft > 4
                            ? t('home.timerTrial.alert.subTitle.middle')
                            : daysLeft === 0
                            ? t('home.timerTrial.alert.subTitle.finish')
                            : t('home.timerTrial.alert.subTitle.lastDay')}
                    </p>
                </div>
            </div>

            <div className={styles['actions']}>
                <a
                    className={styles['assistantBtn']}
                    href={`https://wa.me/${
                        user?.client?.is_free_trial ? WPP_LINK_PLG : WPP_LINK_TRADICIONAL
                    }?text=${encodeURIComponent('INICIANDO CHAT CON EXTENDEAL')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {}}
                    style={{ textDecoration: 'none' }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        style={{ marginBottom: 1 }}
                    >
                        <path
                            d="M8.03353 0C3.64259 0 0.0684557 3.55648 0.0665929 7.9277C0.0661272 9.32499 0.433087 10.6889 1.13022 11.8911L0 16L4.2233 14.8975C5.38704 15.5291 6.69701 15.8619 8.03027 15.8624H8.03353C12.424 15.8624 15.9981 12.3054 16 7.93419C16.0009 5.81578 15.1729 3.82389 13.6688 2.32557C12.1646 0.826787 10.1645 0.000926892 8.03353 0ZM8.03353 14.5235H8.03074C6.84277 14.5235 5.67716 14.2051 4.66057 13.6049L4.41888 13.4622L1.91303 14.1166L2.58176 11.6849L2.42436 11.4355C1.76169 10.3863 1.41149 9.17344 1.41196 7.92863C1.41335 4.29522 4.38396 1.33936 8.03632 1.33936C9.805 1.33982 11.4675 2.02618 12.7174 3.27193C13.9678 4.51721 14.6556 6.1731 14.6551 7.93373C14.6537 11.5671 11.6831 14.5235 8.03353 14.5235ZM11.6654 9.58823C11.4666 9.48905 10.4877 9.00985 10.3051 8.94358C10.1226 8.8773 9.98987 8.8444 9.85715 9.04275C9.72443 9.24111 9.34304 9.68741 9.22708 9.81949C9.11112 9.95203 8.9947 9.96825 8.79586 9.86908C8.59701 9.7699 7.95529 9.56042 7.1953 8.88564C6.60341 8.36056 6.20385 7.71174 6.0879 7.51338C5.97194 7.31503 6.07533 7.20797 6.17545 7.10926C6.26486 7.02074 6.37429 6.878 6.47395 6.76214C6.57361 6.64628 6.60667 6.56378 6.6728 6.43124C6.73892 6.29915 6.70586 6.18329 6.65603 6.08412C6.6062 5.98494 6.20805 5.00985 6.04226 4.61314C5.88067 4.22709 5.71675 4.27899 5.59427 4.27297C5.47832 4.26694 5.3456 4.26602 5.21288 4.26602C5.08016 4.26602 4.86454 4.31561 4.682 4.51396C4.49945 4.71232 3.98533 5.19152 3.98533 6.16661C3.98533 7.1417 4.69876 8.08388 4.79795 8.21597C4.89761 8.34805 6.20152 10.3492 8.19838 11.207C8.67338 11.411 9.04407 11.5328 9.33326 11.6241C9.81012 11.7748 10.2441 11.7534 10.5869 11.7025C10.9692 11.6455 11.7646 11.2233 11.9304 10.7603C12.0962 10.2978 12.0962 9.90106 12.0463 9.8181C11.9965 9.73561 11.8638 9.68602 11.6649 9.58684L11.6654 9.58823Z"
                            fill="#07AD4B"
                        />
                    </svg>
                    <p className={styles['text-wpp']}>{t('home.timerTrial.text.wpp')}</p>
                </a>
                {showClose && (
                    <button className={styles['closeBtn']} onClick={onClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M11.9998 13.4L7.0998 18.3C6.91647 18.4833 6.68314 18.575 6.3998 18.575C6.11647 18.575 5.88314 18.4833 5.6998 18.3C5.51647 18.1167 5.4248 17.8833 5.4248 17.6C5.4248 17.3167 5.51647 17.0833 5.6998 16.9L10.5998 12L5.6998 7.09999C5.51647 6.91665 5.4248 6.68332 5.4248 6.39999C5.4248 6.11665 5.51647 5.88332 5.6998 5.69999C5.88314 5.51665 6.11647 5.42499 6.3998 5.42499C6.68314 5.42499 6.91647 5.51665 7.0998 5.69999L11.9998 10.6L16.8998 5.69999C17.0831 5.51665 17.3165 5.42499 17.5998 5.42499C17.8831 5.42499 18.1165 5.51665 18.2998 5.69999C18.4831 5.88332 18.5748 6.11665 18.5748 6.39999C18.5748 6.68332 18.4831 6.91665 18.2998 7.09999L13.3998 12L18.2998 16.9C18.4831 17.0833 18.5748 17.3167 18.5748 17.6C18.5748 17.8833 18.4831 18.1167 18.2998 18.3C18.1165 18.4833 17.8831 18.575 17.5998 18.575C17.3165 18.575 17.0831 18.4833 16.8998 18.3L11.9998 13.4Z"
                                fill="#40435B"
                            />
                        </svg>
                    </button>
                )}
            </div>
        </MessageWrapper>
    );
};

export default TrialTimerMessage;
